import { useContext, useEffect, useState } from "react"
import { useLocation } from "react-router-dom"

// comps
import EmailVerification from "./components/EmailVerification"
import EnabledMethods from "./components/EnabledMethods"
import Sms from "./components/Sms"
import Totp from "./components/Totp"

import { FirebaseContext } from "../../../firebase"
import { RecaptchaVerifier, getAuth } from "firebase/auth"
import { callCloudFunction } from "../../../helpers/cloudFunctionRequests"

export default function TwoFASettings() {
  const [emailVerified, setEmailVerified] = useState(false)
  const [isUserAvailable, setIsUserAvailable] = useState(false)
  const [current2FAMethod, setCurrent2FAMethod] = useState(null)

  const [enrolledFactors, setEnrolledFactors] = useState([])

  const firebase = useContext(FirebaseContext)

  const location = useLocation()

  // Recaptcha Init
  useEffect(() => {
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear()
      } catch (error) {
        console.warn("Error clearing reCAPTCHA:", error)
      }
      window.recaptchaVerifier = null
    }

    const recaptchaContainer = document.createElement("div")
    recaptchaContainer.id = "recaptcha-container"
    document.body.appendChild(recaptchaContainer)

    window.recaptchaVerifier = new RecaptchaVerifier(
      recaptchaContainer.id,
      {
        size: "invisible",
        callback: function (response) {
          console.log("Recaptcha callback response:", response)
        },
      },
      getAuth()
    )

    window.recaptchaVerifier.render() // render recaptcha

    window.recaptchaVerifier.verify().catch((error) => {
      console.error("Error verifying recaptcha:", error)
    })

    return () => {
      const container = document.getElementById("recaptcha-container")
      if (container) container.remove()
    }
  }, [])

  // Firebase Auth
  useEffect(() => {
    const unsubscribe = firebase.auth.onAuthStateChanged((authUser) => {
      if (authUser) {
        const userEnrolledFactors = authUser?.multiFactor?.enrolledFactors || []
        setEnrolledFactors(userEnrolledFactors)
        if (authUser.emailVerified) setEmailVerified(true)
        setIsUserAvailable(true)
      } else {
        setIsUserAvailable(false)
      }
    })

    // Check if the 'tab' query parameter exists in the URL
    const searchParams = new URLSearchParams(location.search)
    const tabParam = searchParams.get("tab")

    // Set the 'tab' state based on the query parameter
    if (tabParam) {
      setCurrent2FAMethod(tabParam)
    }

    return () => unsubscribe()
  }, [])

  async function reloadUser() {
    await firebase.auth.currentUser.reload()

    const newAuthUser = firebase.auth.currentUser
    console.log("User reloaded: ", newAuthUser)

    if (newAuthUser.emailVerified) setEmailVerified(true)

    const userEnrolledFactors = newAuthUser?.multiFactor?.enrolledFactors || []
    setEnrolledFactors(userEnrolledFactors)
  }

  async function setEnableMfaEnrolledFactor(factorId) {
    try {
      const currentUser = getAuth().currentUser
      const userToken = await currentUser.getIdToken()

      await callCloudFunction(
        "enableMfaEnrolledFactor", 
        { uid: currentUser.uid, preferred_method: factorId }, 
        userToken
      )
    } catch (error) {
      console.error("Error enrolling user:", error)
    }
  }

  const handleConfigNewMethod = () => {
    if (current2FAMethod === null) return null

    const handleEnrolledFactors = (id) =>
      enrolledFactors.find(({ factorId }) => factorId === id)

    return (
      <div>
        <h2>Step 3: Setting Up the 2FA Method</h2>
        {current2FAMethod === "sms" && (
          <Sms
            reloadUser={reloadUser}
            setEnableMfaEnrolledFactor={setEnableMfaEnrolledFactor}
            enrolledFactor={handleEnrolledFactors("phone")}
          />
        )}
        {current2FAMethod === "totp" && (
          <Totp
            reloadUser={reloadUser}
            setEnableMfaEnrolledFactor={setEnableMfaEnrolledFactor}
            enrolledFactor={handleEnrolledFactors("totp")}
          />
        )}
      </div>
    )
  }

  if (!isUserAvailable) return null

  return (
    <div className="main-content">
      <h1>Configuring Two-Factor Authentication for Your Account</h1>
      <EmailVerification
        emailVerified={emailVerified}
        reloadUser={reloadUser}
      />
      <EnabledMethods
        current2FAMethod={current2FAMethod}
        enrolledFactors={enrolledFactors}
        setCurrent2FAMethod={setCurrent2FAMethod}
      />
      {handleConfigNewMethod()}
    </div>
  )
}
